<template>
  <div class="compact-mobile-pagination" :data-testid="`${testIdPrefix}-mobile-pagination`">
    <div class="compact-mobile-pagination__controls">
      <button
        class="page-link compact-mobile-pagination__button"
        :data-testid="`${testIdPrefix}-mobile-first`"
        type="button"
        aria-label="First page"
        :disabled="previousDisabled"
        @click="emitPage(1)"
      >
        &laquo;
      </button>
      <button
        class="page-link compact-mobile-pagination__button"
        :data-testid="`${testIdPrefix}-mobile-prev`"
        type="button"
        aria-label="Previous page"
        :disabled="previousDisabled"
        @click="emitPage(currentPage - 1)"
      >
        &lsaquo;
      </button>
      <span class="page-link compact-mobile-pagination__summary" :data-testid="`${testIdPrefix}-mobile-summary`">
        Page {{ currentPage }} / {{ totalPages }}
      </span>
      <button
        class="page-link compact-mobile-pagination__button"
        :data-testid="`${testIdPrefix}-mobile-next`"
        type="button"
        aria-label="Next page"
        :disabled="nextDisabled"
        @click="emitPage(currentPage + 1)"
      >
        &rsaquo;
      </button>
      <button
        class="page-link compact-mobile-pagination__button"
        :data-testid="`${testIdPrefix}-mobile-last`"
        type="button"
        aria-label="Last page"
        :disabled="nextDisabled"
        @click="emitPage(totalPages)"
      >
        &raquo;
      </button>
    </div>
    <form class="compact-mobile-pagination__jump" @submit.prevent="submitJump">
      <label class="compact-mobile-pagination__label" :for="inputId">Go to page:</label>
      <input
        :id="inputId"
        v-model="jumpPage"
        class="compact-mobile-pagination__input"
        :data-testid="`${testIdPrefix}-mobile-input`"
        type="number"
        inputmode="numeric"
        min="1"
        :max="totalPages"
        :disabled="loading"
      >
      <button
        class="page-link compact-mobile-pagination__go"
        :data-testid="`${testIdPrefix}-mobile-go`"
        type="submit"
        :disabled="loading"
      >
        Go
      </button>
    </form>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  testIdPrefix: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['page-change'])
const jumpPage = ref(String(props.currentPage))
const inputId = computed(() => `${props.testIdPrefix}-mobile-page-input`)
const previousDisabled = computed(() => props.loading || props.currentPage <= 1)
const nextDisabled = computed(() => props.loading || props.currentPage >= props.totalPages)

watch(() => props.currentPage, (page) => {
  jumpPage.value = String(page)
})

function normalizedPage(value) {
  const page = Number.parseInt(value, 10)
  if (!Number.isFinite(page)) return props.currentPage
  return Math.min(Math.max(page, 1), props.totalPages)
}

function emitPage(page) {
  const nextPage = normalizedPage(page)
  if (nextPage === props.currentPage || props.loading) return
  emit('page-change', nextPage)
}

function submitJump() {
  emitPage(jumpPage.value)
}
</script>

<style scoped>
.compact-mobile-pagination {
  display: none;
}

@media (max-width: 440px) {
  .compact-mobile-pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
  }

  .compact-mobile-pagination__controls,
  .compact-mobile-pagination__jump {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .compact-mobile-pagination__button,
  .compact-mobile-pagination__summary,
  .compact-mobile-pagination__go {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 3.8rem;
    min-height: 3.2rem;
    padding: 0.65rem 1.1rem;
    color: var(--color-text);
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .compact-mobile-pagination__button:not(:first-child),
  .compact-mobile-pagination__summary,
  .compact-mobile-pagination__go {
    margin-left: -1px;
  }

  .compact-mobile-pagination__button,
  .compact-mobile-pagination__go {
    cursor: pointer;
  }

  .compact-mobile-pagination__controls .compact-mobile-pagination__button:first-child {
    border-top-left-radius: 0.375rem;
    border-bottom-left-radius: 0.375rem;
  }

  .compact-mobile-pagination__controls .compact-mobile-pagination__button:last-child {
    border-top-right-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
  }

  .compact-mobile-pagination__input {
    border-top-left-radius: 0.375rem;
    border-bottom-left-radius: 0.375rem;
  }

  .compact-mobile-pagination__go {
    border-top-right-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
  }

  .compact-mobile-pagination__button:disabled,
  .compact-mobile-pagination__go:disabled {
    color: var(--color-text-muted);
    opacity: 0.45;
    cursor: not-allowed;
  }

  .compact-mobile-pagination__label {
    color: var(--color-text);
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }

  .compact-mobile-pagination__input {
    width: 7.2rem;
    min-height: 3.2rem;
    margin-left: 0.4rem;
    padding: 0.65rem 0.8rem;
    border: 1px solid var(--color-border);
    background-color: var(--color-btn-surface);
    color: var(--color-text);
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.2;
  }
}
</style>
